import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import type { MotionProps } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import type { WhitelabelPaymentHelpMethod } from "./paymentHelpSteps";

/** Demo values aligned with product payment screenshots (tour only). */
const PAY_DEMO = {
  tier: "Silver",
  price: 998,
  originalPrice: 1050,
  points: 3000,
  usdtApprox: "11.346832",
  cryptoAddress: "0xE2097fe5aB2c8d4F9c1e2B3a4D5e6F7a8B9c0D1e2f3a4b5c6d7e8f9a0b1c2d3e4f5",
  upiId: "icici@ybl",
  bank: {
    accountNumber: "123098123098",
    accountName: "UPI Test",
    ifsc: "",
    bankName: "UPI Test Name",
  },
} as const;

const RED_BORDER = "border border-red-600/95";
const RED_BORDER_FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/80";
const INPUT_BG = "bg-black/80 text-white placeholder:text-zinc-600";
const ACCENT_RED = "rgb(220 38 38)";

/** Same classes as `CreateWhitelabel` offline “Submit request” (theme `--border-color`). */
const WL_OFFLINE_PRIMARY_BTN_CLASS =
  "p-2.5 text-sm text-white rounded-lg border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] font-semibold shadow-[0_2px_12px_color-mix(in_srgb,var(--border-color)_25%,transparent)] transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-45 disabled:cursor-not-allowed";

const PULSE_EASE = [0.42, 0, 0.58, 1] as const;

function pulsePaymentMotion(
  pulseId: string | null,
  controlId: string
): Pick<MotionProps, "animate" | "transition"> {
  const on = pulseId === controlId;
  if (on) {
    return {
      animate: {
        scale: [1, 0.985, 1],
        boxShadow: [
          "0 0 0 0px transparent",
          `0 0 0 3px color-mix(in srgb, ${ACCENT_RED} 55%, transparent)`,
          "0 0 0 0px transparent",
        ],
      },
      transition: { duration: 0.34, ease: PULSE_EASE },
    };
  }
  return {
    animate: { scale: 1 },
    transition: { duration: 0.12, ease: PULSE_EASE },
  };
}

const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

function formatInr(n: number) {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `₹${n}`;
  }
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore tour / insecure context */
  }
}

export type WhitelabelPaymentHelpShellProps = {
  method: WhitelabelPaymentHelpMethod;
  blockPointerForAutopilot?: boolean;
};

export type WhitelabelPaymentHelpShellRef = {
  pulseControl: (selector: string) => Promise<void>;
  setFiatTab: (tab: "upi" | "bank") => void;
  /** Tour autopilot: pretend a screenshot was chosen so Submit enables. */
  simulateFiatProofUploaded: () => void;
  /** Tour autopilot: fire the Submit control (demo only). */
  clickFiatSubmitDemo: () => void;
  /** Tour: clear crypto network and close the dropdown (currency step). */
  clearCryptoCurrencyForTour: () => void;
  /** Tour: open the currency dropdown so options are visible. */
  openCryptoDropdownForTour: () => void;
  /** Tour: choose USDT (ETHEREUM); closes dropdown and reveals address, QR, and USDT estimate. */
  selectUsdtEthereumForTour: () => void;
  /** Tour: same as selectUsdtEthereumForTour (deposit step). */
  selectCryptoCurrencyForTour: () => void;
};

const CRYPTO_USDT_ETHEREUM = "USDT (ETHEREUM)" as const;

const CRYPTO_CURRENCY_OPTIONS = [
  CRYPTO_USDT_ETHEREUM,
  "USDT (TRON)",
  "USDT (BSC)",
  "BITCOIN",
] as const;

export const WhitelabelPaymentHelpShell = forwardRef<
  WhitelabelPaymentHelpShellRef,
  WhitelabelPaymentHelpShellProps
>(function WhitelabelPaymentHelpShell({ method, blockPointerForAutopilot = false }, ref) {
  const navigate = useNavigate();
  const [pulseId, setPulseId] = useState<string | null>(null);
  const [fiatTab, setFiatTabState] = useState<"upi" | "bank">("upi");
  const [cryptoCurrency, setCryptoCurrency] = useState<string>("");
  const [cryptoDropdownOpen, setCryptoDropdownOpen] = useState(false);
  const [fiatProofFileName, setFiatProofFileName] = useState<string | null>(null);
  const fiatSubmitBtnRef = useRef<HTMLButtonElement>(null);

  const pulseControl = useCallback(async (selector: string) => {
    const id = selector.startsWith("#") ? selector.slice(1) : selector;
    setPulseId(id);
    await delay(420);
    setPulseId(null);
    await delay(120);
  }, []);

  const setFiatTab = useCallback((tab: "upi" | "bank") => {
    setFiatTabState(tab);
  }, []);

  const simulateFiatProofUploaded = useCallback(() => {
    setFiatProofFileName("Payment-screenshot-demo.png");
  }, []);

  const clickFiatSubmitDemo = useCallback(() => {
    fiatSubmitBtnRef.current?.click();
  }, []);

  const clearCryptoCurrencyForTour = useCallback(() => {
    setCryptoCurrency("");
    setCryptoDropdownOpen(false);
  }, []);

  const openCryptoDropdownForTour = useCallback(() => {
    setCryptoDropdownOpen(true);
  }, []);

  const selectUsdtEthereumForTour = useCallback(() => {
    setCryptoCurrency(CRYPTO_USDT_ETHEREUM);
    setCryptoDropdownOpen(false);
  }, []);

  const selectCryptoCurrencyForTour = useCallback(() => {
    selectUsdtEthereumForTour();
  }, [selectUsdtEthereumForTour]);

  useImperativeHandle(
    ref,
    () => ({
      pulseControl,
      setFiatTab,
      simulateFiatProofUploaded,
      clickFiatSubmitDemo,
      clearCryptoCurrencyForTour,
      openCryptoDropdownForTour,
      selectUsdtEthereumForTour,
      selectCryptoCurrencyForTour,
    }),
    [
      pulseControl,
      setFiatTab,
      simulateFiatProofUploaded,
      clickFiatSubmitDemo,
      clearCryptoCurrencyForTour,
      openCryptoDropdownForTour,
      selectUsdtEthereumForTour,
      selectCryptoCurrencyForTour,
    ]
  );

  const headerTitle =
    method === "crypto" ? "CRYPTO" : method === "fiat" ? "FIAT" : "Offline payment";

  const isUsdtSelected = cryptoCurrency.includes("USDT");

  const fiatSubmitEnabled = Boolean(fiatProofFileName);

  return (
    <div className="box-border mt-[3.75rem] flex min-h-[calc(100dvh-3.75rem)] flex-col overflow-x-hidden overflow-y-hidden bg-black text-white">
      <div
        className={`mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col px-3 pb-[100px] pt-3 sm:px-4${
          blockPointerForAutopilot ? " pointer-events-none" : ""
        }`}
      >
        <header className="flex min-w-0 shrink-0 items-center gap-1 border-b border-red-900/35 pb-3">
          <button
            type="button"
            onClick={() => navigate("/whitelabel")}
            className="group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10"
            aria-label="Back to whitelabels"
          >
            <ArrowBackIcon sx={{ fontSize: 24, color: "#fff" }} />
          </button>
          <h1 className="m-0 min-w-0 flex-1 text-lg font-bold uppercase tracking-[0.08em] text-white sm:text-xl">
            {headerTitle}
          </h1>
        </header>

        <p className="shrink-0 pt-2 text-center text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Tour preview — no real payment
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] pt-1">
          <div className="space-y-4 pb-20">
            <motion.div
              id="wl-pay-overview"
              className="rounded-lg border border-zinc-800/90 bg-zinc-950/80 px-3 py-2.5"
              {...pulsePaymentMotion(pulseId, "wl-pay-overview")}
            >
              <p className="m-0 text-center text-xs leading-relaxed text-zinc-400">
                {method === "offline" ? (
                  <>
                    Offline requests are for teams who settle outside instant rails. You can still open this help
                    from brands when you need a walkthrough.
                  </>
                ) : (
                  <>
                    Use this flow after <span className="text-zinc-300">creating a brand</span> or for{" "}
                    <span className="text-zinc-300">brand top-up</span>
                  </>
                )}
              </p>
            </motion.div>

            <motion.div
              id="wl-pay-plan-summary"
              className="flex flex-col items-center px-1 pt-1"
              {...pulsePaymentMotion(pulseId, "wl-pay-plan-summary")}
            >
              <p className="m-0 text-base font-semibold text-white">{PAY_DEMO.tier}</p>
              <div className="mt-2 flex flex-wrap items-end justify-center gap-x-2 gap-y-0">
                <span className="text-3xl font-bold leading-none tracking-tight text-white sm:text-[2rem]">
                  {formatInr(PAY_DEMO.price)}
                </span>
                <span className="pb-0.5 text-base text-zinc-500 line-through decoration-zinc-500">
                  {formatInr(PAY_DEMO.originalPrice)}
                </span>
              </div>
              <p className="mt-1.5 text-sm text-zinc-500">
                {PAY_DEMO.points.toLocaleString()} points
              </p>
              {method === "crypto" && isUsdtSelected ? (
                <div className="mt-3 rounded-full border border-emerald-700/50 bg-emerald-950/50 px-3 py-1.5">
                  <span className="text-xs font-semibold text-emerald-400/95">
                    ≈ {PAY_DEMO.usdtApprox} USDT
                  </span>
                </div>
              ) : null}
            </motion.div>

            {method === "crypto" ? (
              <>
                <motion.div
                  id="wl-pay-crypto-currency"
                  className="space-y-3"
                  {...pulsePaymentMotion(pulseId, "wl-pay-crypto-currency")}
                >
                  <div className="relative">
                    <label className="mb-1.5 block text-sm font-medium text-white">Select currency</label>
                    <motion.button
                      id="wl-pay-crypto-dropdown-trigger"
                      type="button"
                      aria-haspopup="listbox"
                      aria-expanded={cryptoDropdownOpen}
                      onClick={() => setCryptoDropdownOpen((o) => !o)}
                      className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-black/80 py-2.5 pl-3 pr-2 text-left text-sm font-medium text-white ${RED_BORDER} ${RED_BORDER_FOCUS}`}
                      {...pulsePaymentMotion(pulseId, "wl-pay-crypto-dropdown-trigger")}
                    >
                      <span className={cryptoCurrency ? "text-white" : "text-zinc-500"}>
                        {cryptoCurrency || "Select network"}
                      </span>
                      <KeyboardArrowDownRoundedIcon
                        className={`shrink-0 text-zinc-400 transition-transform ${cryptoDropdownOpen ? "rotate-180" : ""}`}
                        sx={{ fontSize: 22 }}
                      />
                    </motion.button>

                    {cryptoDropdownOpen ? (
                      <motion.ul
                        id="wl-pay-crypto-dropdown-list"
                        role="listbox"
                        className={`absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-lg ${RED_BORDER} bg-zinc-950 py-1 shadow-lg`}
                        {...pulsePaymentMotion(pulseId, "wl-pay-crypto-dropdown-list")}
                      >
                        {CRYPTO_CURRENCY_OPTIONS.map((opt) => (
                          <li key={opt} role="none">
                            {opt === CRYPTO_USDT_ETHEREUM ? (
                              <motion.button
                                type="button"
                                id="wl-pay-crypto-option-usdt-eth"
                                role="option"
                                aria-selected={cryptoCurrency === opt}
                                onClick={() => {
                                  setCryptoCurrency(opt);
                                  setCryptoDropdownOpen(false);
                                }}
                                className={`flex w-full px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/10 ${
                                  cryptoCurrency === opt ? "bg-white/5 text-white" : "text-zinc-200"
                                }`}
                                {...pulsePaymentMotion(pulseId, "wl-pay-crypto-option-usdt-eth")}
                              >
                                {opt}
                              </motion.button>
                            ) : (
                              <button
                                type="button"
                                role="option"
                                aria-selected={cryptoCurrency === opt}
                                onClick={() => {
                                  setCryptoCurrency(opt);
                                  setCryptoDropdownOpen(false);
                                }}
                                className={`flex w-full px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/10 ${
                                  cryptoCurrency === opt ? "bg-white/5 text-white" : "text-zinc-200"
                                }`}
                              >
                                {opt}
                              </button>
                            )}
                          </li>
                        ))}
                      </motion.ul>
                    ) : null}

                    {!isUsdtSelected ? (
                      <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                        Choose a USDT network — your deposit address, QR code, and USDT estimate appear after you
                        select.
                      </p>
                    ) : null}
                    {cryptoCurrency === "BITCOIN" ? (
                      <p className="mt-2 text-xs leading-relaxed text-amber-200/90">
                        For this tour, pick a USDT option above to see the demo deposit address and QR.
                      </p>
                    ) : null}
                  </div>
                </motion.div>

                {isUsdtSelected ? (
                  <motion.div
                    id="wl-pay-crypto-deposit"
                    className="space-y-4"
                    {...pulsePaymentMotion(pulseId, "wl-pay-crypto-deposit")}
                  >
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-white">
                        Your deposit address
                        {cryptoCurrency.includes("USDT") ? " (USDT)" : ""}
                      </label>
                      <div
                        className={`flex min-h-[2.75rem] items-center gap-2 rounded-lg px-2.5 py-2 ${RED_BORDER} ${INPUT_BG}`}
                      >
                        <span className="min-w-0 flex-1 truncate font-mono text-xs text-zinc-200 sm:text-sm">
                          {PAY_DEMO.cryptoAddress.slice(0, 18)}…
                        </span>
                        <button
                          type="button"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
                          aria-label="Copy address"
                          onClick={() => void copyText(PAY_DEMO.cryptoAddress)}
                        >
                          <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="rounded-xl bg-white p-3 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
                        <QRCode
                          value={PAY_DEMO.cryptoAddress}
                          size={196}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                        />
                      </div>
                      <p className="mt-4 max-w-[20rem] text-center text-xs leading-relaxed text-zinc-500">
                        Scan the QR code or copy the address to complete your transfer.
                      </p>
                    </div>
                  </motion.div>
                ) : null}

                <motion.div
                  id="wl-pay-crypto-done"
                  className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                  {...pulsePaymentMotion(pulseId, "wl-pay-crypto-done")}
                >
                  <p className="m-0 text-center text-xs leading-relaxed text-zinc-400">
                    When the network confirms your transfer, The status under My brands.
                  </p>
                </motion.div>
              </>
            ) : null}

            {method === "fiat" ? (
              <>
                <motion.div
                  id="wl-pay-fiat-tabs"
                  className="flex border-b border-zinc-800"
                  {...pulsePaymentMotion(pulseId, "wl-pay-fiat-tabs")}
                >
                  <button
                    type="button"
                    onClick={() => setFiatTabState("upi")}
                    className={`relative flex-1 py-3 text-center text-sm font-bold uppercase tracking-wide transition-colors ${
                      fiatTab === "upi" ? "text-white" : "text-zinc-500"
                    }`}
                  >
                    UPI
                    {fiatTab === "upi" ? (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-amber-500/90" />
                    ) : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFiatTabState("bank")}
                    className={`relative flex-1 py-3 text-center text-sm font-bold uppercase tracking-wide transition-colors ${
                      fiatTab === "bank" ? "text-white" : "text-zinc-500"
                    }`}
                  >
                    BANK TRANSFER
                    {fiatTab === "bank" ? (
                      <span className="absolute bottom-0 left-3 right-3 h-1 rounded-full bg-red-600" />
                    ) : null}
                  </button>
                </motion.div>

                <motion.div
                  id="wl-pay-fiat-upi"
                  className={`space-y-4 ${fiatTab !== "upi" ? "hidden" : ""}`}
                  {...pulsePaymentMotion(pulseId, "wl-pay-fiat-upi")}
                >
                  <div className="flex justify-center">
                    <div className="rounded-xl bg-white p-3 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
                      <QRCode
                        value={`upi://pay?pa=${PAY_DEMO.upiId}&pn=Demo&am=${PAY_DEMO.originalPrice}`}
                        size={188}
                        style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      />
                    </div>
                  </div>
                  <div
                    className={`flex min-h-[2.75rem] items-center gap-2 rounded-lg px-2.5 py-2 ${RED_BORDER} ${INPUT_BG}`}
                  >
                    <span className="min-w-0 flex-1 text-sm text-zinc-100">
                      <span className="text-zinc-500">UPI ID: </span>
                      {PAY_DEMO.upiId}
                    </span>
                    <button
                      type="button"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
                      aria-label="Copy UPI ID"
                      onClick={() => void copyText(PAY_DEMO.upiId)}
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  id="wl-pay-fiat-bank"
                  className={`space-y-0 ${fiatTab !== "bank" ? "hidden" : ""}`}
                  {...pulsePaymentMotion(pulseId, "wl-pay-fiat-bank")}
                >
                  {(
                    [
                      { label: "Account Number", value: PAY_DEMO.bank.accountNumber },
                      { label: "Account Name", value: PAY_DEMO.bank.accountName },
                      { label: "IFSC Code", value: PAY_DEMO.bank.ifsc },
                      { label: "Bank Name", value: PAY_DEMO.bank.bankName },
                    ] as const
                  ).map((row) => {
                    const displayValue =
                      row.label === "IFSC Code" && row.value === "" ? "—" : row.value;
                    const canCopy = row.value.length > 0;
                    return (
                    <div
                      key={row.label}
                      className={`flex min-h-[3rem] items-center justify-between gap-2 border-b border-zinc-800/90 py-2.5 last:border-b-0`}
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                          {row.label}
                        </p>
                        <p className="mt-0.5 truncate font-mono text-sm text-white">{displayValue}</p>
                      </div>
                      {canCopy ? (
                        <button
                          type="button"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
                          aria-label={`Copy ${row.label}`}
                          onClick={() => void copyText(row.value)}
                        >
                          <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                        </button>
                      ) : (
                        <span className="h-9 w-9 shrink-0" aria-hidden />
                      )}
                    </div>
                  );
                  })}
                </motion.div>

                <motion.div
                  id="wl-pay-fiat-proof"
                  className="space-y-3 pt-1"
                  {...pulsePaymentMotion(pulseId, "wl-pay-fiat-proof")}
                >
                  <p className="text-sm font-medium text-white">
                    Payment screenshot <span className="text-red-500">*</span>
                  </p>
                  <label
                    id="wl-pay-fiat-upload"
                    className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-red-700 py-3.5 text-sm font-bold text-white shadow-[0_2px_16px_rgba(220,38,38,0.35)] transition hover:bg-red-600 active:scale-[0.99] ${RED_BORDER_FOCUS}`}
                  >
                    <CloudUploadRoundedIcon sx={{ fontSize: 22 }} />
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      tabIndex={-1}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        setFiatProofFileName(f ? f.name : null);
                      }}
                    />
                  </label>
                  {fiatProofFileName ? (
                    <p className="truncate text-center text-xs text-zinc-500">{fiatProofFileName}</p>
                  ) : null}
                  <motion.button
                    ref={fiatSubmitBtnRef}
                    id="wl-pay-fiat-submit"
                    type="button"
                    disabled={!fiatSubmitEnabled}
                    className={`w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-wide transition ${
                      fiatSubmitEnabled
                        ? "bg-red-800 text-white hover:bg-red-700"
                        : "cursor-not-allowed bg-zinc-900/90 text-zinc-600"
                    }`}
                    {...pulsePaymentMotion(pulseId, "wl-pay-fiat-submit")}
                    onClick={() => {
                      if (!fiatSubmitEnabled) return;
                      /* Tour / demo only — no API call */
                    }}
                  >
                    Submit
                  </motion.button>
                </motion.div>
              </>
            ) : null}

            {method === "offline" ? (
              <div className="mx-auto mt-2 w-full max-w-[320px]">
                <motion.div
                  id="wl-pay-offline-info"
                  className="rounded-lg border border-border-color bg-inputbox-color/70 px-4 py-3"
                  {...pulsePaymentMotion(pulseId, "wl-pay-offline-info")}
                >
                  <p className="text-sm font-semibold text-zinc-100">Raise offline payment request</p>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-300">
                    Submit this request and our team will reach out to help you complete the payment offline.
                  </p>
                </motion.div>
                <motion.button
                  id="wl-pay-offline-submit"
                  type="button"
                  className={`mt-4 w-full ${WL_OFFLINE_PRIMARY_BTN_CLASS}`}
                  {...pulsePaymentMotion(pulseId, "wl-pay-offline-submit")}
                  onClick={() => {
                    /* Tour / demo only — no API call */
                  }}
                >
                  Submit request
                </motion.button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});
